// PayNow Service - API calls for PayNow functionality

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Get PayNow configuration for a specific salon
 * @param {string} salonId - The salon ID
 * @returns {Promise} PayNow configuration data
 */
export const getPayNowConfig = async (salonId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/config/${salonId}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch PayNow configuration');
    }

    return data.config;
  } catch (error) {
    console.error('Error fetching PayNow config:', error);
    throw error;
  }
};

/**
 * Save PayNow configuration for a salon
 * @param {string} salonId - The salon ID
 * @param {object} configData - Configuration data
 * @returns {Promise} Save result
 */
export const savePayNowConfig = async (salonId, configData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/config/${salonId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(configData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to save PayNow configuration');
    }

    return data;
  } catch (error) {
    console.error('Error saving PayNow config:', error);
    throw error;
  }
};

/**
 * Generate a unique transaction reference for PayNow payment
 * @param {string} salonId - The salon ID
 * @param {string} transactionId - Optional existing transaction ID
 * @param {number} amount - Transaction amount
 * @returns {Promise} Reference and expiry data
 */
export const generatePayNowReference = async (salonId, transactionId, amount) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/generate-reference`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        salonId,
        transactionId,
        amount
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate PayNow reference');
    }

    return {
      reference: data.reference,
      expiresAt: data.expiresAt
    };
  } catch (error) {
    console.error('Error generating PayNow reference:', error);
    throw error;
  }
};

/**
 * Check payment status for a pending transaction
 * @param {string} reference - The transaction reference
 * @returns {Promise} Payment status data
 */
export const checkPaymentStatus = async (reference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/check-status/${reference}`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to check payment status');
    }

    return {
      status: data.status,
      reference: data.reference,
      amount: data.amount,
      createdAt: data.createdAt,
      completedAt: data.completedAt,
      isExpired: data.isExpired
    };
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

/**
 * Manually verify and mark a PayNow transaction as completed
 * @param {string} transactionId - The transaction ID
 * @param {string} salonId - The salon ID
 * @param {string} reference - Optional PayNow reference
 * @returns {Promise} Verification result
 */
export const verifyPayNowPayment = async (transactionId, salonId, reference = null) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        transactionId,
        salonId,
        reference
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to verify payment');
    }

    return data;
  } catch (error) {
    console.error('Error verifying PayNow payment:', error);
    throw error;
  }
};

/**
 * Poll for payment status updates
 * @param {string} reference - The transaction reference
 * @param {function} onStatusChange - Callback when status changes
 * @param {number} intervalMs - Polling interval in milliseconds (default: 3000)
 * @param {number} maxAttempts - Maximum polling attempts (default: 60)
 * @returns {object} Polling controller with stop() method
 */
export const pollPaymentStatus = (reference, onStatusChange, intervalMs = 3000, maxAttempts = 60) => {
  let attempts = 0;
  let stopped = false;
  let lastStatus = 'pending';

  const pollInterval = setInterval(async () => {
    if (stopped) {
      clearInterval(pollInterval);
      return;
    }

    attempts++;

    try {
      const statusData = await checkPaymentStatus(reference);

      // Check if status changed
      if (statusData.status !== lastStatus) {
        lastStatus = statusData.status;
        onStatusChange(statusData);
      }

      // Stop polling if completed or expired
      if (statusData.status === 'completed' || statusData.isExpired) {
        clearInterval(pollInterval);
        stopped = true;
      }

      // Stop if max attempts reached
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        stopped = true;
        onStatusChange({
          ...statusData,
          timeout: true
        });
      }
    } catch (error) {
      console.error('Error polling payment status:', error);
      // Continue polling on error
    }
  }, intervalMs);

  // Return controller object
  return {
    stop: () => {
      stopped = true;
      clearInterval(pollInterval);
    }
  };
};

/**
 * Get all PayNow configurations (Super Admin only)
 * @returns {Promise} Array of PayNow configurations
 */
export const getAllPayNowConfigs = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/config`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch PayNow configurations');
    }

    return data.configs || [];
  } catch (error) {
    console.error('Error fetching all PayNow configs:', error);
    throw error;
  }
};

/**
 * Generate a dynamic PayNow QR code via OCBC API (through backend)
 * @param {string} salonId - The salon ID
 * @param {string} transactionId - The transaction ID
 * @param {number} amount - Payment amount
 * @param {string} reference - PayNow reference string
 * @returns {Promise<Object>} { qrCodeData, isDynamic, fallback, reference, amount, expiresAt }
 */
export const generateDynamicQR = async (salonId, transactionId, amount, reference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/generate-qr`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ salonId, transactionId, amount, reference })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to generate QR code');
    }

    return data;
  } catch (error) {
    console.error('Error generating dynamic QR:', error);
    throw error;
  }
};

/**
 * Cancel a pending PayNow transaction
 * @param {string} transactionId
 * @param {string} salonId
 * @returns {Promise<Object>}
 */
export const cancelPayNowTransaction = async (transactionId, salonId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/paynow/cancel-transaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transactionId, salonId })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to cancel transaction');
    }

    return data;
  } catch (error) {
    console.error('Error cancelling PayNow transaction:', error);
    throw error;
  }
};
